import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { environment } from '@env';
import { filter } from 'rxjs';
import { StructuredDataService } from './structured-data.service';

export interface SeoData {
  title?: string;
  description?: string;
  canonicalPath?: string;
  robots?: string;
  image?: string;
}

const DEFAULT_SEO: Required<SeoData> = {
  title: 'PT Glori Global Makmur | General Supplier & Hardware Batam',
  description:
    'General supplier & hardware Batam: peralatan las, alat keselamatan kerja, material konstruksi, peralatan listrik, pipa dan katup untuk kebutuhan industri Anda.',
  canonicalPath: '/',
  robots: 'index, follow',
  image: '/assets/images/logo/logo.webp',
};

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly document = inject(DOCUMENT);
  private readonly structuredData = inject(StructuredDataService);
  private initialized = false;

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.structuredData.clearTransient();
        this.applyRouteSeo();
      });

    queueMicrotask(() => this.applyRouteSeo());
  }

  setSeo(seo: SeoData): void {
    const canonicalPath =
      seo.canonicalPath ?? this.pathWithoutQuery(this.router.url);
    this.applySeo({ ...seo, canonicalPath });
  }

  private applyRouteSeo(): void {
    let current: ActivatedRoute | null = this.route;
    let seo: SeoData | undefined;

    while (current) {
      const routeSeo = current.snapshot.data['seo'] as SeoData | undefined;
      if (routeSeo) seo = routeSeo;
      current = current.firstChild;
    }

    this.applySeo({
      ...seo,
      canonicalPath:
        seo?.canonicalPath ?? this.pathWithoutQuery(this.router.url),
    });
  }

  private applySeo(seo: SeoData): void {
    const defined = Object.fromEntries(
      Object.entries(seo).filter(([, v]) => v !== undefined)
    ) as SeoData;
    const merged = { ...DEFAULT_SEO, ...defined };
    const description = this.normalizeText(merged.description, 160);
    const canonicalUrl = this.absoluteUrl(merged.canonicalPath);
    const imageUrl = this.absoluteUrl(merged.image);

    this.title.setTitle(merged.title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: merged.robots });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({
      property: 'og:site_name',
      content: 'PT Glori Global Makmur',
    });
    this.meta.updateTag({ property: 'og:title', content: merged.title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: merged.title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
    this.updateCanonical(canonicalUrl);
    this.updateHreflang(canonicalUrl);
  }

  private updateCanonical(url: string): void {
    let link = this.document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  private updateHreflang(url: string): void {
    this.upsertHreflang('id-ID', url);
    this.upsertHreflang('x-default', url);
  }

  private upsertHreflang(lang: string, url: string): void {
    const selector = `link[rel="alternate"][hreflang="${lang}"]`;
    let link = this.document.querySelector<HTMLLinkElement>(selector);
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private absoluteUrl(pathOrUrl: string): string {
    if (/^https?:\/\//.test(pathOrUrl)) return pathOrUrl;

    const siteUrl = environment.SITE_URL.replace(/\/+$/, '');
    const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${siteUrl}${path}`;
  }

  private pathWithoutQuery(url: string): string {
    const path = url.split(/[?#]/)[0] || '/';
    return path.length > 1 ? path.replace(/\/+$/, '') : path;
  }

  private normalizeText(text: string, maxLength: number): string {
    const normalized = text.replace(/\s+/g, ' ').trim();
    if (normalized.length <= maxLength) return normalized;

    return `${normalized.slice(0, maxLength - 1).trim()}...`;
  }
}
